import React, { useState, useEffect } from 'react';
import { Copy, RefreshCw, Check, Hash, Clock, Globe, Zap, List, Settings } from 'lucide-react';
import { PageTracker } from '../../../components/PageTracker.tsx';
import '../../../styles/dev-utils-theme.css';

interface GeneratedUUID {
  id: string;
  value: string;
  version: string;
  timestamp: string;
}

function UuidGenerator() {
  const [uuids, setUuids] = useState<GeneratedUUID[]>([]);
  const [selectedVersion, setSelectedVersion] = useState('v4');
  const [quantity, setQuantity] = useState(1);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [upperCase, setUpperCase] = useState(false);
  const [removeDashes, setRemoveDashes] = useState(false);

  // UUID v4 generator (random)
  const generateUUIDv4 = (): string => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : ((r & 0x3) | 0x8);
      return v.toString(16);
    });
  };

  // UUID v1 generator (timestamp-based)
  const generateUUIDv1 = (): string => {
    const timestamp = Date.now();
    const timeHex = timestamp.toString(16).padStart(12, '0');
    const randomBytes = Array.from({length: 6}, () => Math.floor(Math.random() * 256).toString(16).padStart(2, '0')).join('');
    
    return `${timeHex.slice(-8)}-${timeHex.slice(-12, -8)}-1${timeHex.slice(-3)}-8${randomBytes.slice(0, 3)}-${randomBytes.slice(3)}`;
  };

  // UUID v3 generator (MD5 hash-based) - simplified version
  const generateUUIDv3 = (): string => {
    const chars = '0123456789abcdef';
    let result = '';
    for (let i = 0; i < 32; i++) {
      result += chars[Math.floor(Math.random() * 16)];
    }
    return `${result.slice(0, 8)}-${result.slice(8, 12)}-3${result.slice(13, 16)}-${result.slice(16, 20)}-${result.slice(20)}`;
  };

  // Nil UUID
  const generateNilUUID = (): string => {
    return '00000000-0000-0000-0000-000000000000';
  };

  // Max UUID
  const generateMaxUUID = (): string => {
    return 'ffffffff-ffff-ffff-ffff-ffffffffffff';
  };

  const formatUUID = (uuid: string): string => {
    let formatted = uuid;
    if (removeDashes) {
      formatted = formatted.replace(/-/g, '');
    }
    if (upperCase) {
      formatted = formatted.toUpperCase();
    }
    return formatted;
  };

  const generateUUID = (version: string): string => {
    let uuid = '';
    switch (version) {
      case 'v1':
        uuid = generateUUIDv1();
        break;
      case 'v3':
        uuid = generateUUIDv3();
        break;
      case 'v4':
        uuid = generateUUIDv4();
        break;
      case 'nil':
        uuid = generateNilUUID();
        break;
      case 'max':
        uuid = generateMaxUUID();
        break;
      default:
        uuid = generateUUIDv4();
    }
    return formatUUID(uuid);
  };

  const handleGenerate = () => {
    const newUuids: GeneratedUUID[] = [];
    for (let i = 0; i < quantity; i++) {
      const uuid = generateUUID(selectedVersion);
      newUuids.push({
        id: Math.random().toString(36).substr(2, 9),
        value: uuid,
        version: selectedVersion,
        timestamp: new Date().toLocaleString()
      });
    }
    setUuids(prev => [...newUuids, ...prev].slice(0, 50)); // Keep only last 50
  };

  const copyToClipboard = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const clearHistory = () => {
    setUuids([]);
  };

  const getVersionInfo = (version: string) => {
    const info = {
      v1: { name: 'Version 1', desc: 'Time-based UUID', icon: Clock },
      v3: { name: 'Version 3', desc: 'MD5 hash-based UUID', icon: Hash },
      v4: { name: 'Version 4', desc: 'Random UUID', icon: Zap },
      nil: { name: 'Nil UUID', desc: 'All zeros UUID', icon: Globe },
      max: { name: 'Max UUID', desc: 'All ones UUID', icon: Globe }
    };
    return info[version as keyof typeof info] || info.v4;
  };

  // Auto-generate on component mount
  useEffect(() => {
    if (uuids.length === 0) {
      handleGenerate();
    }
  }, [selectedVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  const versionInfo = getVersionInfo(selectedVersion);
  const IconComponent = versionInfo.icon;

  return (
    <div className="dev-utils-page" style={{ minHeight: "100vh", background: "var(--lyra-color-surface-2)", fontFamily: "'DM Sans', system-ui, sans-serif", padding: "28px 28px 48px" }}>
      <PageTracker pageTitle="UUID Generator" category="dev-utils" />
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          <Hash size={20} style={{ color: "var(--app-primary-mid)" }} />
          <span style={{ fontSize: 22, fontWeight: 600, color: "var(--lyra-color-text)", fontFamily: "DM Sans, system-ui, sans-serif" }}>UUID Generator</span>
        </div>
        <p style={{ fontSize: 13, color: "var(--app-text-2)", margin: 0 }}>Generate various types of UUIDs (Universally Unique Identifiers) for your projects</p>
      </div>

      {/* Configuration Panel */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* UUID Version Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              UUID Version
            </label>
            <select
              value={selectedVersion}
              onChange={(e) => setSelectedVersion(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="v4">Version 4 (Random)</option>
              <option value="v1">Version 1 (Timestamp)</option>
              <option value="v3">Version 3 (MD5 Hash)</option>
              <option value="nil">Nil UUID (All Zeros)</option>
              <option value="max">Max UUID (All Ones)</option>
            </select>
          </div>

          {/* Quantity */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Quantity
            </label>
            <input
              type="number"
              min="1"
              max="20"
              value={quantity}
              onChange={(e) => setQuantity(Math.max(1, Math.min(20, parseInt(e.target.value) || 1)))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            />
          </div>

          {/* Format Options */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Format Options
            </label>
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={upperCase}
                  onChange={(e) => setUpperCase(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">Uppercase</span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={removeDashes}
                  onChange={(e) => setRemoveDashes(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">Remove dashes</span>
              </label>
            </div>
          </div>

          {/* Generate Button */}
          <div className="flex flex-col justify-end">
            <button
              onClick={handleGenerate}
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition duration-200 flex items-center justify-center gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Generate
            </button>
          </div>
        </div>
      </div>

      {/* Version Info */}
      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 mb-6">
        <div className="flex items-center gap-3">
          <IconComponent className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          <div>
            <h3 className="font-medium text-blue-900 dark:text-blue-100">{versionInfo.name}</h3>
            <p className="text-sm text-blue-700 dark:text-blue-300">{versionInfo.desc}</p>
          </div>
        </div>
      </div>

      {/* Generated UUIDs */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <List className="h-5 w-5" />
              Generated UUIDs ({uuids.length})
            </h2>
            {uuids.length > 0 && (
              <button
                onClick={clearHistory}
                className="text-sm text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
              >
                Clear History
              </button>
            )}
          </div>
        </div>

        <div className="p-6">
          {uuids.length === 0 ? (
            <div className="text-center py-8">
              <Hash className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400">No UUIDs generated yet</p>
              <button
                onClick={handleGenerate}
                className="mt-4 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition duration-200"
              >
                Generate Your First UUID
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {uuids.map((uuid) => (
                <div
                  key={uuid.id}
                  className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <code className="font-mono text-lg text-gray-900 dark:text-white break-all">
                        {uuid.value}
                      </code>
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                        {uuid.version.toUpperCase()}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Generated: {uuid.timestamp}
                    </p>
                  </div>
                  <button
                    onClick={() => copyToClipboard(uuid.value, uuid.id)}
                    className="ml-4 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                    title="Copy to clipboard"
                  >
                    {copiedId === uuid.id ? (
                      <Check className="h-5 w-5 text-green-600" />
                    ) : (
                      <Copy className="h-5 w-5" />
                    )}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Information Panel */}
      <div className="mt-6 bg-gray-50 dark:bg-gray-800 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <Settings className="h-5 w-5" />
          UUID Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
          <div>
            <h4 className="font-medium text-gray-900 dark:text-white mb-2">UUID Versions:</h4>
            <ul className="space-y-1 text-gray-600 dark:text-gray-400">
              <li><strong>Version 1:</strong> Time-based, includes timestamp and MAC address</li>
              <li><strong>Version 3:</strong> Name-based using MD5 hashing</li>
              <li><strong>Version 4:</strong> Random or pseudo-random (most common)</li>
              <li><strong>Nil:</strong> Special UUID with all bits set to zero</li>
              <li><strong>Max:</strong> Special UUID with all bits set to one</li>
            </ul>
          </div>
          <div>
            <h4 className="font-medium text-gray-900 dark:text-white mb-2">Use Cases:</h4>
            <ul className="space-y-1 text-gray-600 dark:text-gray-400">
              <li>• Database primary keys</li>
              <li>• API request identifiers</li>
              <li>• File and resource naming</li>
              <li>• Session and transaction IDs</li>
              <li>• Distributed system coordination</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UuidGenerator;